import { LegalLinks } from "components";
import { useBrand } from "hooks/useBrand";
import { useSeo } from "hooks/useSeo";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Paths from "routes/Paths";

import type { LegalPageLayoutProps, LegalSection } from "./legal.types";

import styles from "./LegalPageLayout.module.css";

const headingPattern = /^(\d+)\.\s*(.*)$/;

/** Canonical path per legal document, keyed by the same `page` value used for its copy. */
const legalPaths: Record<string, string> = {
	privacyPolicy: Paths.privacyPolicy,
	termsOfService: Paths.termsOfService,
	refundPolicy: Paths.refundPolicy,
	cookiePolicy: Paths.cookiePolicy,
	rightsPolicy: Paths.rightsPolicy
};

/**
 * Anchors come from the section key rather than its heading. The headings arrive asynchronously
 * with the namespace, so heading-derived ids would not exist on first paint and a `#section` link
 * into the page would land nowhere; keys are known synchronously and stay put across locales.
 */
const getSectionId = (key: string): string =>
	key
		.replace(/([a-z0-9])([A-Z])/g, "$1-$2")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

const splitHeading = (heading: string): { number: string | null; text: string } => {
	const match = headingPattern.exec(heading);

	return match ? { number: match[1], text: match[2] } : { number: null, text: heading };
};

const LegalPageLayout = ({ children, intro, page, sections }: LegalPageLayoutProps) => {
	const { t, ready } = useTranslation(["legal", "common", "seo"]);
	const { brand } = useBrand();

	const title = t(`legal:${page}.title`);

	// Every legal page shared the generic "Tree Tier" tab title; payment-gateway reviews look these
	// pages up by name, so each one names itself. Until the `legal` namespace lands over HTTP `t`
	// echoes the raw key, and useSeo leaves the title alone for a null — so wait for it. The
	// description comes from `seo`, which is fetched at boot and is therefore always ready.
	// `page` doubles as the key in both namespaces and in Paths, so all four documents stay in step.
	useSeo({
		title: ready ? title : null,
		description: t(`seo:${page}.description`, { brand }),
		canonicalPath: legalPaths[page] ?? null
	});

	// Everything the policy prose interpolates: the product name, and the contact details quoted
	// inside the documents. The latter are held in `common:company` so these pages and the Contact
	// page can never drift apart on the registered address or phone.
	const copyValues = {
		address: t("common:company.address"),
		brand,
		company: t("common:company.name"),
		email: t("common:company.email"),
		phone: t("common:company.phone")
	};

	return (
		<main className={styles.page}>
			<header className={styles.header}>
				<Link aria-label={t("common:brandHome", { brand })} className={styles.brand} to={Paths.root}>
					<img alt="" className={styles.brandMark} src="/assets/landing/main-logo.png" />
					<span>{brand}</span>
				</Link>
				<Link className={styles.homeLink} to={Paths.root}>
					{t("common:backToHome")}
				</Link>
			</header>

			<section className={styles.hero}>
				<div className={styles.heroInner}>
					<p className={styles.eyebrow}>{t("legal:eyebrow", { brand })}</p>
					<h1>{title}</h1>
					<div aria-hidden="true" className={styles.flourish} />
					<p className={styles.effectiveDate}>
						{t("legal:effectiveDate", { date: t(`legal:${page}.effectiveDate`) })}
					</p>
				</div>
			</section>

			<div className={styles.body}>
				<nav aria-label={t("legal:onThisPage")} className={styles.toc}>
					<h2>{t("legal:onThisPage")}</h2>
					{sections.map((section: LegalSection) => {
						const { number, text } = splitHeading(t(`legal:${page}.sections.${section.key}.heading`));

						return (
							<a key={section.key} href={`#${getSectionId(section.key)}`}>
								{number ? <span>{number.padStart(2, "0")}</span> : null}
								{text}
							</a>
						);
					})}
				</nav>

				<article className={styles.card}>
					{intro.map((paragraphKey: string) => (
						<p key={paragraphKey} className={styles.intro}>
							{t(`legal:${page}.intro.${paragraphKey}`, copyValues)}
						</p>
					))}

					{sections.map((section: LegalSection) => {
						const { number, text } = splitHeading(t(`legal:${page}.sections.${section.key}.heading`));
						const sectionPath = `legal:${page}.sections.${section.key}`;

						return (
							<section key={section.key} className={styles.section} id={getSectionId(section.key)}>
								<h2>
									{number ? (
										<span aria-hidden="true" className={styles.sectionNumber}>
											{number}
										</span>
									) : null}
									{text}
								</h2>
								{section.paragraphs.map((paragraphKey: string) => (
									<p key={paragraphKey}>
										{t(`${sectionPath}.paragraphs.${paragraphKey}`, copyValues)}
									</p>
								))}
								{section.listItems ? (
									<ul>
										{section.listItems.map((itemKey: string) => (
											<li key={itemKey}>
												{t(`${sectionPath}.listItems.${itemKey}`, copyValues)}
											</li>
										))}
									</ul>
								) : null}
								{section.trailingParagraphs?.map((paragraphKey: string) => (
									<p key={paragraphKey}>
										{t(`${sectionPath}.trailingParagraphs.${paragraphKey}`, copyValues)}
									</p>
								))}
							</section>
						);
					})}

					{children}
				</article>
			</div>

			<footer className={styles.footer}>
				<span>{t("common:copyright")}</span>
				<nav aria-label={t("common:legalPagesLabel")} className={styles.footerNav}>
					<LegalLinks />
				</nav>
			</footer>
		</main>
	);
};

export default LegalPageLayout;
